import * as THREE from 'three';

// Cube-face ↔ equirect geometry, driven by the marker's WORLD direction and the equirect's own
// (confirmed-correct) world frame — NOT the SfM camera-helper poses. The helper poses are yaw-rotated
// relative to the cube-face images, so using them to pick/project a face lands one face off. The equirect
// panorama is world-aligned (ModelViewer wraps it with only a +90° X tilt, yaw 0), so a world direction
// maps to a panorama pixel by:
//   x_frac = atan2(-d.y, d.x) / 2PI     (wrapped 0..1, around world Z)
//   y_frac = acos(d.z) / PI             (0 = zenith/top, 1 = nadir — level horizon)
const TWO_PI = Math.PI * 2;
const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);
const frac01 = (u) => ((u % 1) + 1) % 1;

export function dirToEquirect(d) {
  return {
    x: frac01(Math.atan2(-d.y, d.x) / TWO_PI),
    y: Math.acos(clamp(d.z, -1, 1)) / Math.PI,
  };
}

// The four horizontal cube faces, by azimuth bucket, as WORLD directions consistent with dirToEquirect
// (bucket i is centred at panorama u = i/4):
//   0 → +X, 1 → -Y, 2 → -X, 3 → +Y
const SIDE_DIRS = [
  new THREE.Vector3(1, 0, 0),
  new THREE.Vector3(0, -1, 0),
  new THREE.Vector3(-1, 0, 0),
  new THREE.Vector3(0, 1, 0),
];

// Maps each world face to the cube-face FILENAME label, anchored on the equirect layout: FRONT is the
// centre of the panorama (u≈0.5), LEFT is left-of-centre (u≈0.25), RIGHT is right-of-centre (u≈0.75),
// and BACK wraps the two edges (u≈0/1). Buckets are ordered by panorama u: [u≈0, u≈0.25, u≈0.5, u≈0.75]
// = [+X, -Y, -X, +Y] in world. If LEFT/RIGHT come out mirrored on a capture, swap those two entries.
const FACE_RING = ['back', 'left', 'front', 'right'];
// Canonical up/down labels must match cubeFaceName()'s normalisation (top→up, bottom→down) so the
// capture's face lookup finds them.
const UP_LABEL = 'up';
const DOWN_LABEL = 'down';
const ALIAS = { up: 'up', top: 'up', down: 'down', bottom: 'down' };

// World direction → the cube-face label whose IMAGE shows that direction.
export function faceLabelForWorldDir(d) {
  const v = d.clone().normalize();
  if (Math.abs(v.z) >= Math.abs(v.x) && Math.abs(v.z) >= Math.abs(v.y)) {
    return v.z > 0 ? UP_LABEL : DOWN_LABEL;
  }
  const bucket = (Math.round((frac01(Math.atan2(-v.y, v.x) / TWO_PI)) * 4)) % 4;
  return FACE_RING[bucket];
}

// Cube-face label → the WORLD direction that face's camera looks along + its up vector.
function faceFrame(label) {
  const f = ALIAS[(label || '').toLowerCase()] || (label || '').toLowerCase();
  if (f === 'up') return { dir: new THREE.Vector3(0, 0, 1), up: new THREE.Vector3(0, -1, 0) };
  if (f === 'down') return { dir: new THREE.Vector3(0, 0, -1), up: new THREE.Vector3(0, 1, 0) };
  const bucket = FACE_RING.indexOf(f);
  if (bucket < 0) return null;
  return { dir: SIDE_DIRS[bucket].clone(), up: new THREE.Vector3(0, 0, 1) };   // level horizon
}

// A canonical 90°-FOV square cube-face camera at `center`, looking along a world face direction. This is
// the camera model the cube-face IMAGES follow (FlowEngine renders seamless 90° faces), so projecting /
// unprojecting through it agrees with the frame pixels — unlike the real (distorted, mis-yawed) helper.
function buildFaceCamera(center, label) {
  const frame = faceFrame(label);
  if (!frame) return null;
  const cam = new THREE.PerspectiveCamera(90, 1, 0.01, 1000);
  cam.position.copy(center);
  cam.up.copy(frame.up);
  cam.lookAt(center.clone().add(frame.dir));
  cam.updateMatrixWorld(true);
  cam.updateProjectionMatrix();
  return cam;
}

/**
 * Project a world point onto the cube face, returning NORMALISED face coords (0..1, top-left origin),
 * or null if it is behind / outside the 90° face.
 */
export function projectWorldToFace(center, label, worldPoint) {
  const cam = buildFaceCamera(center, label);
  if (!cam) return null;
  const p = worldPoint.clone();
  const view = p.clone().applyMatrix4(cam.matrixWorldInverse);
  if (view.z >= 0) return null;                      // behind the face plane — unusable
  // A box can straddle a seam: clamp corners that overflow onto a neighbouring face to THIS face's edge
  // (so the bbox extends to the boundary) rather than dropping them, which could collapse the box.
  const ndc = p.project(cam);
  return { x: (clamp(ndc.x, -1, 1) + 1) / 2, y: (1 - clamp(ndc.y, -1, 1)) / 2 };
}

/**
 * Project a world point onto a cube face described by an EXPLICIT world frame ({@link dir} = the
 * direction the face looks along, {@link up} = its in-plane up), instead of the equirect-anchored
 * FACE_RING. Used for legacy cube-only bubbles, whose faces are placed by the renderer's own
 * CUBE_FACE_LAYOUT — so selection and projection share the frame the user actually drew on.
 * Returns NORMALISED face coords (0..1, top-left origin), or null if behind the face.
 */
export function projectWorldToFaceDir(center, dir, up, worldPoint) {
  const cam = new THREE.PerspectiveCamera(90, 1, 0.01, 1000);
  cam.position.copy(center);
  cam.up.copy(up);
  cam.lookAt(center.clone().add(dir));
  cam.updateMatrixWorld(true);
  cam.updateProjectionMatrix();
  const p = worldPoint.clone();
  const view = p.clone().applyMatrix4(cam.matrixWorldInverse);
  if (view.z >= 0) return null;                      // behind the face plane — unusable
  const ndc = p.project(cam);
  return { x: (clamp(ndc.x, -1, 1) + 1) / 2, y: (1 - clamp(ndc.y, -1, 1)) / 2 };
}

/**
 * Map a NORMALISED face bbox (0..1) onto the equirect panorama, using the face's canonical world camera.
 * @param {string} faceLabel  cube-face label (front/back/left/right/top/bottom) from the source filename
 * @param {{left:number,top:number,width:number,height:number}} bbox  NORMALISED face coords (0..1)
 * @returns {{left:number,top:number,width:number,height:number}|null}  equirect UV box (0..1), or null
 */
export function equirectBoxFromFace(faceLabel, bbox) {
  if (!bbox) return null;
  const cam = buildFaceCamera(new THREE.Vector3(0, 0, 0), faceLabel);   // directions are position-free
  if (!cam) return null;
  const camPos = cam.position.clone();
  const dirOf = (nx, ny) =>
    new THREE.Vector3(nx * 2 - 1, -(ny * 2 - 1), 0.5).unproject(cam).sub(camPos).normalize();

  const x0 = bbox.left, x1 = bbox.left + bbox.width;
  const y0 = bbox.top, y1 = bbox.top + bbox.height;
  const dirs = [];
  for (const px of [x0, (x0 + x1) / 2, x1]) for (const py of [y0, (y0 + y1) / 2, y1]) dirs.push(dirOf(px, py));
  return equirectBoxOfDirs(dirs);
}

/**
 * Equirect UV box directly bounding a set of WORLD points as seen from `center` — no cube face involved.
 * This is the seamless path used when a region is drawn on the 360 bubble itself.
 */
export function equirectBoxFromWorldPoints(center, worldPoints) {
  if (!worldPoints?.length) return null;
  const dirs = worldPoints.map(p => p.clone().sub(center).normalize());
  return equirectBoxOfDirs(dirs);
}

// Bounding equirect UV box of a set of unit directions, handling the ±180° wrap seam.
function equirectBoxOfDirs(dirs) {
  const pts = dirs.map(dirToEquirect);
  let xsFrac = pts.map(p => p.x);
  let minX = Math.min(...xsFrac), maxX = Math.max(...xsFrac);
  if (maxX - minX > 0.5) {                            // straddles the seam → shift the left side
    xsFrac = xsFrac.map(x => (x < 0.5 ? x + 1 : x));
    minX = Math.min(...xsFrac); maxX = Math.max(...xsFrac);
  }
  const minY = Math.min(...pts.map(p => p.y));
  const maxY = Math.max(...pts.map(p => p.y));
  const width = maxX - minX;
  if (!(width > 0) || width > 0.6) return null;

  return {
    left: frac01(minX),
    top: clamp(minY, 0, 1),
    width: Math.min(Math.max(width, 0.005), 1),
    height: Math.min(Math.max(maxY - minY, 0.005), 1),
  };
}
